import { useEffect, useRef } from "react";
import { useAronApiBase, useAronMessages } from "../api/aronApi";
import { useAronWorkbenchStore } from "../store/aronWorkbenchStore";
import { isQuestionToUser, lastSettledAssistant, pendingWrites } from "../utils/attention";

/**
 * Turns what happened in ONE open conversation into the two things the user has
 * to notice: a badge on its tab (ARON asked / proposed / answered) and the
 * approval modal for a freshly proposed database write.
 *
 * Only changes seen while this tab was open count. Everything already in the
 * thread when the tab opens is history — reopening yesterday's conversation must
 * not flash a badge, and must not re-pop a modal the user already postponed.
 */
export function useAronTabSignals(convId: string | null, active: boolean) {
  const apiBase = useAronApiBase();
  const { data: messages } = useAronMessages(convId);
  const raiseAttention = useAronWorkbenchStore((s) => s.raiseAttention);
  const clearAttention = useAronWorkbenchStore((s) => s.clearAttention);
  const requestApproval = useAronWorkbenchStore((s) => s.requestApproval);

  const baseRef = useRef<{
    convId: string;
    lastId: string | null;
    writes: Set<string>;
  } | null>(null);

  useEffect(() => {
    if (!convId || !messages) return;
    const settled = lastSettledAssistant(messages);
    const writes = pendingWrites(messages);
    const base = baseRef.current;
    if (!base || base.convId !== convId) {
      baseRef.current = {
        convId,
        lastId: settled?.id ?? null,
        writes: new Set(writes.map((w) => w.id)),
      };
      return;
    }
    // Wait for the turn to settle: proposals stream in one by one, and a modal
    // popping mid-answer would gate a decision the user cannot yet judge.
    const live = messages.some(
      (m) => m.role === "assistant" && (m.status === "streaming" || m.status === "pending"),
    );
    if (live) return;

    const fresh = writes.filter((w) => !base.writes.has(w.id));
    fresh.forEach((w) => base.writes.add(w.id));
    if (fresh.length) {
      requestApproval({ convId, writeId: fresh[0].id, apiBase });
    }
    if (settled && settled.id !== base.lastId) {
      base.lastId = settled.id;
      if (!active) {
        raiseAttention(
          convId,
          writes.length
            ? "write"
            : isQuestionToUser(settled.text)
              ? "question"
              : "done",
        );
      }
    } else if (fresh.length && !active) {
      raiseAttention(convId, "write");
    }
  }, [convId, messages, active, apiBase, raiseAttention, requestApproval]);

  // Looking at the tab IS acknowledging it.
  useEffect(() => {
    if (active && convId) clearAttention(convId);
  }, [active, convId, clearAttention]);
}
